// df-53c89a39b0e6: one owner for "move the person after a server login".
//
// After a server login several parts of the app may each move the person to another page: warm
// resume Phase B routes in a person its own login unlocked, a login retry re-emits the user and
// re-runs the post-login boot (whose navigation start-up sends the person through the page
// filter), and a page that opened before the server's subscription record arrived (the subscribe
// and unsubscribe pages) may decide to leave once it does. Two set-root navigations started
// together can overlap, so these used to avoid each other by reading each other's flags, and each
// new page had to learn every other path's timing.
//
// Instead, a part of the app that is about to move the person itself takes a CLAIM and releases
// it once that move is over. A page that would like the person moved makes a REQUEST. With no
// claim held the request runs at once. While a claim is held the request waits, and it runs when
// the last claim is released, but only if the page that made it is still on screen: when the
// claimant moved the person, the page has left and the request is dropped, and the page is told
// so it can ask again if the person comes back to it. A claim that is never released expires, so
// a lost release costs a wait instead of a page that never leaves.
//
// Pure apart from the timer, which is injected, so it runs under node for unit tests (same
// pattern as nav-restore-coordination.ts). See design-docs/warm-resume.md, "One owner for the
// route-in after a server login".

/** how long a claim may be held before it is dropped. Long enough for a retried login's
 *  re-run boot to finish its navigation start-up, and for Phase B's own server login */
export const POST_LOGIN_ROUTE_CLAIM_EXPIRY_MS = 60_000;

export interface PostLoginRouteTimers {
  set(callback: () => void, ms: number): unknown;
  clear(handle: unknown): void;
  now(): number;
}

export interface PostLoginRouteHooks {
  /** a claim expired while a request was waiting on it */
  onClaimExpired(reason: string, heldMs: number): void;
  /** a request's route rejected */
  onRouteFailed(reason: string, error: unknown): void;
}

export interface PostLoginRouteClaim {
  /** the claimant's own move is over (or it decided not to move anyone). Safe to call twice */
  release(): void;
}

interface HeldClaim {
  reason: string;
  takenAt: number;
  expiryTimer: unknown;
}

interface PendingRequest {
  reason: string;
  route: () => Promise<void>;
  stillWanted: () => boolean;
  onDropped?: () => void;
}

export class PostLoginRouteCoordinator {
  private nextClaimId = 1;
  private readonly claims = new Map<number, HeldClaim>();
  private pending: PendingRequest | null = null;

  constructor(
    private readonly timers: PostLoginRouteTimers,
    private readonly hooks: PostLoginRouteHooks,
    private readonly expiryMs = POST_LOGIN_ROUTE_CLAIM_EXPIRY_MS,
  ) { }

  /** a part of the app is about to move the person itself */
  claim(reason: string): PostLoginRouteClaim {
    const id = this.nextClaimId++;
    const held: HeldClaim = {
      reason,
      takenAt: this.timers.now(),
      expiryTimer: this.timers.set(() => this.expire(id), this.expiryMs),
    };
    this.claims.set(id, held);
    return { release: () => this.release(id) };
  }

  /** a page would like the person moved. `stillWanted` answers whether the page is still on
   *  screen; a request that waited is dropped when it says no, and then `onDropped` runs, so the
   *  page can ask again once it is back on screen */
  request(reason: string, route: () => Promise<void>, stillWanted: () => boolean, onDropped?: () => void): void {
    if (this.claims.size === 0) {
      console.log(`PostLoginRoute: ${reason} moves the person now`);
      this.run({ reason, route, stillWanted, onDropped });
      return;
    }
    // one pending destination: only one page is on screen, so a newer request replaces an older one
    console.log(`PostLoginRoute: ${reason} waits for ${this.describeClaims()}`);
    this.pending = { reason, route, stillWanted, onDropped };
  }

  isClaimed(): boolean {
    return this.claims.size > 0;
  }

  private release(id: number): void {
    const claim = this.claims.get(id);
    if (!claim) return;
    this.timers.clear(claim.expiryTimer);
    this.claims.delete(id);
    this.runPendingIfFree();
  }

  private expire(id: number): void {
    const claim = this.claims.get(id);
    if (!claim) return;
    this.claims.delete(id);
    // only worth a report when somebody was kept waiting by it
    if (this.pending) this.hooks.onClaimExpired(claim.reason, this.timers.now() - claim.takenAt);
    this.runPendingIfFree();
  }

  private runPendingIfFree(): void {
    if (this.claims.size > 0 || !this.pending) return;
    const pending = this.pending;
    this.pending = null;
    if (!pending.stillWanted()) {
      console.log(`PostLoginRoute: ${pending.reason} dropped; its page is no longer on screen`);
      pending.onDropped?.();
      return;
    }
    console.log(`PostLoginRoute: ${pending.reason} moves the person now that nothing else does`);
    this.run(pending);
  }

  private run(request: PendingRequest): void {
    void request.route().catch(err => this.hooks.onRouteFailed(request.reason, err));
  }

  private describeClaims(): string {
    return [...this.claims.values()].map(c => c.reason).join(', ');
  }
}
